import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import DoctorLogin from './DoctorLogin';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import ForcePasswordChange from '@medical/shared/components/ForcePasswordChange';
import { verifyStoredSession } from '@medical/shared/utils/verifyStoredSession';

const DoctorProtectedRoute = ({ children }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Store referrer URL when accessing doctor login page (before authentication)
    const checkAndStoreReferrer = () => {
      const token = localStorage.getItem('doctorToken');
      const existingReferrer = localStorage.getItem('doctorReferrer');
      
      // Only store referrer if not authenticated and not already stored
      if (!token && !existingReferrer) {
        // Get the referrer from document.referrer or use the current page before doctor route
        let referrer = document.referrer;
        
        // If no referrer or referrer is the doctor page itself, use home page
        if (!referrer || referrer.includes('/doctor') || referrer === window.location.href) {
          referrer = '/';
        }
        
        // Extract just the pathname if it's a full URL
        try {
          const url = new URL(referrer);
          referrer = url.pathname + url.search;
        } catch (e) {
          // If it's already a path, use it as is
          if (referrer.startsWith('/')) {
            // Already a path, keep it
          } else {
            referrer = '/';
          }
        }
        
        localStorage.setItem('doctorReferrer', referrer);
      }
    };
    
    checkAndStoreReferrer();
    checkDoctorAuthStatus();

    // Listen for storage changes (for logout from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === 'doctorToken' || e.key === null) {
        checkDoctorAuthStatus();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom logout event
    const handleLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
    };
    window.addEventListener('doctorLogout', handleLogout);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('doctorLogout', handleLogout);
    };
  }, []);

  const checkDoctorAuthStatus = async () => {
    try {
      const verified = await verifyStoredSession({
        tokenKey: 'doctorToken',
        refreshKey: 'doctorRefreshToken',
        userKey: 'doctorUser',
        role: 'doctor'
      });
      setUser(verified);
      setIsAuthenticated(Boolean(verified));
    } catch (error) {
      console.error('Doctor auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // If logout is in progress, redirect to login page instead of showing doctor login
  if (!isAuthenticated) {
    const isLoggingOut = localStorage.getItem('doctorLoggingOut');
    if (isLoggingOut) {
      localStorage.removeItem('doctorLoggingOut');
      window.location.href = '/login';
      return <LoadingSpinner />;
    }
    return <DoctorLogin onLoginSuccess={handleLoginSuccess} />;
  }

  if (user?.mustChangePassword) {
    return (
      <ForcePasswordChange
        accountLabel={user.doctorId || user.email}
        onSignOut={() => {
          ['doctorToken', 'doctorRefreshToken', 'doctorUser'].forEach((key) => localStorage.removeItem(key));
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setIsAuthenticated(false);
        }}
        onChanged={() => {
          const next = { ...user, mustChangePassword: false };
          setUser(next);
          localStorage.setItem('doctorUser', JSON.stringify(next));
        }}
      />
    );
  }

  return children;
};

export default DoctorProtectedRoute;

